import { Service, computed, effect, signal } from '@angular/core';

export type Theme = 'light' | 'dark' | 'system';

const THEME_STORAGE_KEY = 'savant.portal.theme';

@Service()
export class ThemeService {
  private readonly preference = signal<Theme>(readStoredTheme());

  readonly currentTheme = computed<'light' | 'dark'>(() => {
    const preference = this.preference();
    return preference === 'system' ? systemTheme() : preference;
  });

  constructor() {
    effect(() => applyTheme(this.currentTheme()));

    if (isBrowser() && window.matchMedia) {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        if (this.preference() === 'system') this.preference.set('system');
      });
    }
  }

  setTheme(theme: Theme): void {
    this.preference.set(theme);
    writeStoredTheme(theme);
  }

  getTheme(): Theme {
    return this.preference();
  }
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function systemTheme(): 'light' | 'dark' {
  if (!isBrowser() || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: 'light' | 'dark'): void {
  if (!isBrowser()) return;
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.style.colorScheme = theme;
}

function readStoredTheme(): Theme {
  if (!isBrowser()) return 'system';
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {}
  return 'system';
}

function writeStoredTheme(theme: Theme): void {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {}
}
